import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Target,
  TrendingUp,
  DollarSign,
  Users,
  HelpCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import leadSourceMasterService from '../../services/leadSourceMasterService'
import { formatRupeeAmount, formatINR } from '../../utils/currencyFormat';

const LeadSourceMaster = () => {
  const navigate = useNavigate()
  const [leadSources, setLeadSources] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [showHelp, setShowHelp] = useState(false)
  const [filters, setFilters] = useState({
    category: '',
    type: '',
    status: ''
  })
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  })

  const loadData = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: searchTerm,
        ...filters
      }
      
      const response = await leadSourceMasterService.getAllLeadSources(params)
      
      if (response.success) {
        setLeadSources(response.data.leadSources)
        setPagination(response.data.pagination)
      } else {
        toast.error(response.message || 'Failed to load lead sources')
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load lead sources')
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const response = await leadSourceMasterService.getLeadSourceStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  useEffect(() => {
    loadData()
    loadStats()
  }, [pagination.currentPage, searchTerm, filters])

  const handleAdd = () => {
    navigate('/crm/lead-source-master/new')
  }

  const handleEdit = (leadSource) => {
    navigate(`/crm/lead-source-master/edit/${leadSource._id}`)
  }

  const handleView = (leadSource) => {
    navigate(`/crm/lead-source-master/view/${leadSource._id}`)
  }

  const handleDelete = (leadSource) => {
    setDeleteItem(leadSource)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await leadSourceMasterService.deleteLeadSource(deleteItem._id)
      
      if (response.success) {
        toast.success('Lead source deleted successfully')
        loadData() // Reload data
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete lead source')
      }
    } catch (error) {
      console.error('Error deleting lead source:', error)
      toast.error('Failed to delete lead source')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Please select lead sources to delete')
      return
    }

    try {
      const deletePromises = selectedRows.map(row => 
        leadSourceMasterService.deleteLeadSource(row._id)
      )
      
      await Promise.all(deletePromises)
      toast.success(`${selectedRows.length} lead sources deleted successfully`)
      loadData() // Reload data
      setSelectedRows([])
    } catch (error) {
      console.error('Error deleting lead sources:', error)
      toast.error('Failed to delete selected lead sources')
    }
  }

  // Define columns for the data table - Essential columns only
  const columns = [
    {
      key: 'leadSourceCode',
      label: 'Code',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          <Target size={14} className="text-blue-500" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'leadSourceName',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">{value}</div>
      )
    },
    {
      key: 'leadSourceCategory',
      label: 'Category',
      sortable: true,
      render: (value) => {
        const categoryColors = {
          'Digital Marketing': 'bg-blue-100 text-blue-800',
          'Traditional Marketing': 'bg-green-100 text-green-800',
          'Referral': 'bg-purple-100 text-purple-800',
          'Partnership': 'bg-orange-100 text-orange-800',
          'Event': 'bg-pink-100 text-pink-800',
          'Cold Outreach': 'bg-red-100 text-red-800',
          'Content Marketing': 'bg-indigo-100 text-indigo-800',
          'Social Media': 'bg-cyan-100 text-cyan-800',
          'Other': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'leadSourceType',
      label: 'Type',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <div className={`w-2 h-2 rounded-full ${value === 'Online' ? 'bg-green-500' : value === 'Offline' ? 'bg-blue-500' : 'bg-yellow-500'}`}></div>
          <span>{value}</span>
        </div>
      )
    },
    {
      key: 'totalLeadsGenerated',
      label: 'Leads',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <Users size={14} className="text-gray-400" />
          <span className="font-medium">{value || 0}</span>
        </div>
      )
    },
    {
      key: 'totalRevenue',
      label: 'Revenue',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-1">
          <DollarSign size={14} className="text-gray-400" />
          <span className="font-medium">{value ? formatRupeeAmount(value) : formatRupeeAmount(0)}</span>
        </div>
      )
    },
    {
      key: 'leadSourceStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Active': 'bg-green-100 text-green-800',
          'Inactive': 'bg-gray-100 text-gray-800',
          'Suspended': 'bg-red-100 text-red-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    }
  ]

  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Lead Source Master',
      path: '/crm/lead-source-master',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>What is Lead Source Master?</h3>
      <p>Lead Source Master helps you <strong>define and manage where your leads come from</strong>—such as digital marketing, referrals, events, or cold outreach. Each source can have a category, type (Online/Offline), and you can track how many leads and how much revenue each source generates.</p>
      <h3>How to use</h3>
      <p><strong>Add Lead Source</strong> – Click the blue &quot;Add Lead Source&quot; button to create a new source (code, name, category, type, and optional description).</p>
      <p><strong>Metrics</strong> – The cards show Total Lead Sources, Leads Generated, Leads Converted, and Total Revenue across all sources.</p>
      <p><strong>Table</strong> – Search and sort by Code, Name, Category, Type, Leads, Revenue, and Status. Use the action menu on each row to View, Edit, or Delete a lead source.</p>
      <h3>Categories &amp; status</h3>
      <p>Use categories like Digital Marketing, Referral, Event, or Other to group sources. Set status to Active, Inactive, or Suspended to control visibility and reporting.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Lead Source Master</h1>
          <p>Manage lead generation sources and track performance metrics</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Add Lead Source
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{stats.totalLeadSources || 0}</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalLeadSources || 0}</div>
          <div className="metric-title">Total Lead Sources</div>
          <div className="metric-subtitle">All sources</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{stats.totalLeadsGenerated || 0}</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalLeadsGenerated || 0}</div>
          <div className="metric-title">Leads Generated</div>
          <div className="metric-subtitle">Total leads</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+{stats.totalLeadsConverted || 0}</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalLeadsConverted || 0}</div>
          <div className="metric-title">Leads Converted</div>
          <div className="metric-subtitle">Successful conversions</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+₹{stats.totalRevenue || 0}</span>
            </div>
          </div>
          <div className="metric-value">₹{stats.totalRevenue || 0}</div>
          <div className="metric-title">Total Revenue</div>
          <div className="metric-subtitle">From all sources</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-container">
        <DataTable
          data={leadSources}
          columns={columns}
          loading={loading}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          pagination={pagination}
          onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
          title="Lead Sources"
          searchPlaceholder="Search lead sources..."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Lead Source"
        message={`Are you sure you want to delete "${deleteItem?.leadSourceName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Lead Source Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default LeadSourceMaster
